import * as v from "valibot";
import { rpcContract } from "../rpc";

// --- Creator CRUD ---

export const createCommunityRpcContract = rpcContract({
  name: "create-community",
  paramsSchema: v.object({
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(50)),
    description: v.optional(v.pipe(v.string(), v.maxLength(250))),
    thumbnail_file_id: v.optional(v.string()),
    cta_text: v.optional(v.string()),
    language: v.optional(v.string()),
    content_tier: v.optional(v.picklist(["free", "premium"])),
    fee_cents: v.optional(v.number()),
    live_event_ids: v.optional(v.array(v.string())),
    show_member_count: v.optional(v.boolean()),
  }),
  resultSchema: v.object({
    community: v.object({
      id: v.string(),
      title: v.string(),
      status: v.string(),
      created_at: v.string(),
    }),
  }),
});

export type CreateCommunityParams = v.InferOutput<(typeof createCommunityRpcContract)["paramsSchema"]>;
export type CreateCommunityResult = v.InferOutput<(typeof createCommunityRpcContract)["resultSchema"]>;

export const updateCommunityRpcContract = rpcContract({
  name: "update-community",
  paramsSchema: v.object({
    community_id: v.string(),
    title: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(50))),
    description: v.optional(v.pipe(v.string(), v.maxLength(250))),
    thumbnail_file_id: v.optional(v.nullable(v.string())),
    cta_text: v.optional(v.nullable(v.string())),
    language: v.optional(v.nullable(v.string())),
    content_tier: v.optional(v.picklist(["free", "premium"])),
    fee_cents: v.optional(v.nullable(v.number())),
    live_event_ids: v.optional(v.array(v.string())),
    status: v.optional(v.picklist(["draft", "published", "archived"])),
    show_member_count: v.optional(v.boolean()),
  }),
  resultSchema: v.object({
    community: v.object({
      id: v.string(),
      title: v.string(),
      status: v.string(),
      updated_at: v.string(),
    }),
  }),
});

export type UpdateCommunityParams = v.InferOutput<(typeof updateCommunityRpcContract)["paramsSchema"]>;
export type UpdateCommunityResult = v.InferOutput<(typeof updateCommunityRpcContract)["resultSchema"]>;

export const deleteCommunityRpcContract = rpcContract({
  name: "delete-community",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type DeleteCommunityParams = v.InferOutput<(typeof deleteCommunityRpcContract)["paramsSchema"]>;
export type DeleteCommunityResult = v.InferOutput<(typeof deleteCommunityRpcContract)["resultSchema"]>;

export const getCommunitiesRpcContract = rpcContract({
  name: "get-communities",
  paramsSchema: v.object({}),
  resultSchema: v.object({
    communities: v.array(
      v.object({
        id: v.string(),
        title: v.string(),
        description: v.nullable(v.string()),
        thumbnail_url: v.nullable(v.string()),
        cta_text: v.nullable(v.string()),
        language: v.nullable(v.string()),
        content_tier: v.string(),
        fee_cents: v.nullable(v.number()),
        status: v.string(),
        priority: v.number(),
        published_at: v.nullable(v.string()),
        created_at: v.string(),
        updated_at: v.string(),
        show_member_count: v.boolean(),
        member_count: v.number(),
        chat_message_count: v.number(),
        link_clicks: v.number(),
        discount_clicks: v.number(),
      }),
    ),
    counts: v.object({
      draft: v.number(),
      published: v.number(),
      archived: v.number(),
      total: v.number(),
    }),
  }),
});

export type GetCommunitiesParams = v.InferOutput<(typeof getCommunitiesRpcContract)["paramsSchema"]>;
export type GetCommunitiesResult = v.InferOutput<(typeof getCommunitiesRpcContract)["resultSchema"]>;
export type Community = GetCommunitiesResult["communities"][0];

export const archiveCommunityRpcContract = rpcContract({
  name: "archive-community",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type ArchiveCommunityParams = v.InferOutput<(typeof archiveCommunityRpcContract)["paramsSchema"]>;
export type ArchiveCommunityResult = v.InferOutput<(typeof archiveCommunityRpcContract)["resultSchema"]>;

export const duplicateCommunityRpcContract = rpcContract({
  name: "duplicate-community",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    community: v.object({
      id: v.string(),
      title: v.string(),
      status: v.string(),
      created_at: v.string(),
    }),
  }),
});

export type DuplicateCommunityParams = v.InferOutput<(typeof duplicateCommunityRpcContract)["paramsSchema"]>;
export type DuplicateCommunityResult = v.InferOutput<(typeof duplicateCommunityRpcContract)["resultSchema"]>;

// --- Event Linking ---

export const linkCommunityEventRpcContract = rpcContract({
  name: "link-community-event",
  paramsSchema: v.object({
    community_id: v.string(),
    live_event_id: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type LinkCommunityEventParams = v.InferOutput<(typeof linkCommunityEventRpcContract)["paramsSchema"]>;
export type LinkCommunityEventResult = v.InferOutput<(typeof linkCommunityEventRpcContract)["resultSchema"]>;

export const unlinkCommunityEventRpcContract = rpcContract({
  name: "unlink-community-event",
  paramsSchema: v.object({
    community_id: v.string(),
    live_event_id: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type UnlinkCommunityEventParams = v.InferOutput<(typeof unlinkCommunityEventRpcContract)["paramsSchema"]>;
export type UnlinkCommunityEventResult = v.InferOutput<(typeof unlinkCommunityEventRpcContract)["resultSchema"]>;

export const getCommunityEventsRpcContract = rpcContract({
  name: "get-community-events",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    events: v.array(
      v.object({
        id: v.string(),
        title: v.string(),
        status: v.string(),
        content_tier: v.string(),
        fee_cents: v.nullable(v.number()),
        scheduled_start_time: v.string(),
        thumbnail_url: v.nullable(v.string()),
        cta_text: v.nullable(v.string()),
        hasAccess: v.boolean(),
        influencer_handle: v.nullable(v.string()),
        community_only: v.boolean(),
      }),
    ),
  }),
});

export type GetCommunityEventsParams = v.InferOutput<(typeof getCommunityEventsRpcContract)["paramsSchema"]>;
export type GetCommunityEventsResult = v.InferOutput<(typeof getCommunityEventsRpcContract)["resultSchema"]>;

// --- Public / Fan ---

export const getPublicCommunitiesRpcContract = rpcContract({
  name: "get-public-communities",
  paramsSchema: v.object({
    handle: v.string(),
  }),
  resultSchema: v.object({
    communities: v.array(
      v.object({
        id: v.string(),
        title: v.string(),
        description: v.nullable(v.string()),
        thumbnail_url: v.nullable(v.string()),
        cta_text: v.nullable(v.string()),
        language: v.nullable(v.string()),
        content_tier: v.string(),
        fee_cents: v.nullable(v.number()),
        member_count: v.nullable(v.number()),
        is_member: v.boolean(),
        is_co_promoted: v.optional(v.boolean()),
        owner_handle: v.optional(v.nullable(v.string())),
        owner_display_name: v.optional(v.nullable(v.string())),
        owner_avatar_url: v.optional(v.nullable(v.string())),
        collaborators: v.optional(
          v.array(
            v.object({
              handle: v.string(),
              displayName: v.nullable(v.string()),
              avatarUrl: v.nullable(v.string()),
            }),
          ),
        ),
      }),
    ),
  }),
});

export type GetPublicCommunitiesParams = v.InferOutput<(typeof getPublicCommunitiesRpcContract)["paramsSchema"]>;
export type GetPublicCommunitiesResult = v.InferOutput<(typeof getPublicCommunitiesRpcContract)["resultSchema"]>;

export const getPublicCommunityRpcContract = rpcContract({
  name: "get-public-community",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    community: v.nullable(
      v.object({
        id: v.string(),
        title: v.string(),
        description: v.nullable(v.string()),
        thumbnail_url: v.nullable(v.string()),
        cta_text: v.nullable(v.string()),
        language: v.nullable(v.string()),
        content_tier: v.string(),
        fee_cents: v.nullable(v.number()),
        member_count: v.nullable(v.number()),
        is_member: v.boolean(),
        member_position: v.nullable(v.number()),
        is_co_promoted: v.optional(v.boolean()),
        owner_handle: v.optional(v.nullable(v.string())),
        owner_display_name: v.optional(v.nullable(v.string())),
        owner_avatar_url: v.optional(v.nullable(v.string())),
        influencer_id: v.string(),
        collaborators: v.optional(
          v.array(
            v.object({
              handle: v.string(),
              displayName: v.nullable(v.string()),
              avatarUrl: v.nullable(v.string()),
            }),
          ),
        ),
      }),
    ),
  }),
});

export type GetPublicCommunityParams = v.InferOutput<(typeof getPublicCommunityRpcContract)["paramsSchema"]>;
export type GetPublicCommunityResult = v.InferOutput<(typeof getPublicCommunityRpcContract)["resultSchema"]>;

export const joinCommunityRpcContract = rpcContract({
  name: "join-community",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
    joined_at: v.optional(v.string()),
  }),
});

export type JoinCommunityParams = v.InferOutput<(typeof joinCommunityRpcContract)["paramsSchema"]>;
export type JoinCommunityResult = v.InferOutput<(typeof joinCommunityRpcContract)["resultSchema"]>;

// --- Community Chat ---

const newAttachmentSchema = v.object({
  fileId: v.pipe(v.string(), v.uuid()),
  originalName: v.pipe(v.string(), v.minLength(1), v.maxLength(255)),
  sizeBytes: v.pipe(v.number(), v.minValue(1)),
});

const resolvedAttachmentSchema = v.object({
  fileId: v.string(),
  url: v.string(),
  mimeType: v.string(),
  originalName: v.string(),
  sizeBytes: v.number(),
});

export const sendCommunityChatMessageRpcContract = rpcContract({
  name: "send-community-chat-message",
  paramsSchema: v.object({
    community_id: v.string(),
    content: v.pipe(v.string(), v.maxLength(1000, "Message too long")),
    attachments: v.optional(v.array(newAttachmentSchema)),
  }),
  resultSchema: v.object({
    success: v.boolean(),
    message_id: v.optional(v.string()),
    sent_at: v.optional(v.string()),
    attachments: v.optional(v.array(resolvedAttachmentSchema)),
  }),
});

export type SendCommunityChatMessageParams = v.InferOutput<
  (typeof sendCommunityChatMessageRpcContract)["paramsSchema"]
>;
export type SendCommunityChatMessageResult = v.InferOutput<
  (typeof sendCommunityChatMessageRpcContract)["resultSchema"]
>;

export type CommunityChatAttachment = v.InferOutput<typeof resolvedAttachmentSchema>;

export const getCommunityChatMessagesRpcContract = rpcContract({
  name: "get-community-chat-messages",
  paramsSchema: v.object({
    community_id: v.string(),
    limit: v.optional(v.number()),
    before: v.optional(v.pipe(v.string(), v.uuid())),
  }),
  resultSchema: v.object({
    messages: v.array(
      v.object({
        id: v.string(),
        userId: v.string(),
        userName: v.string(),
        userAvatarUrl: v.nullable(v.string()),
        content: v.string(),
        sentAt: v.string(),
        attachments: v.array(resolvedAttachmentSchema),
        authorIsProtected: v.boolean(),
      }),
    ),
    hasMore: v.boolean(),
    isBanned: v.boolean(),
  }),
});

export type GetCommunityChatMessagesParams = v.InferOutput<
  (typeof getCommunityChatMessagesRpcContract)["paramsSchema"]
>;
export type GetCommunityChatMessagesResult = v.InferOutput<
  (typeof getCommunityChatMessagesRpcContract)["resultSchema"]
>;

export const markCommunityChatReadRpcContract = rpcContract({
  name: "mark-community-chat-read",
  paramsSchema: v.object({
    community_id: v.string(),
  }),
  resultSchema: v.object({
    success: v.boolean(),
  }),
});

export type MarkCommunityChatReadParams = v.InferOutput<(typeof markCommunityChatReadRpcContract)["paramsSchema"]>;
export type MarkCommunityChatReadResult = v.InferOutput<(typeof markCommunityChatReadRpcContract)["resultSchema"]>;
